/**
 * RPE easing numbers -> Phira tween ids, and Phira's resolution of an event's easing fields (F§6.4, F§6.5,
 * P§1.5, R§2.1).
 *
 *   id    = RPE_TWEEN_MAP[max(easingType, 1)], or RPE_TWEEN_MAP[0] (= linear) when the index is >= 30
 *   left  = clamp(easingLeft, 0, 1), right = clamp(easingRight, 0, 1)            (f32)
 *   bezier != 0                                              -> bezier (points from the bezier registry)
 *   id <= 2 or (|left| < 1e-5 and |right - 1| < 1e-5) or left >= right -> static tween id
 *   otherwise                                                -> clamped tween (id, left..right)
 *
 * extra.json keyframes (P§5.1, P§1.5) resolve differently: the slice is NOT clamped to [0, 1], there is no
 * linear shortcut and no `left >= right` check; the clamped tween is used whenever |left| >= 1e-5 or
 * |right - 1| >= 1e-5.
 */

import { LINEAR_TWEEN } from './tweens';
import type { BezierPoints, PhiraTweenDesc, TweenId } from './types';

const fr = Math.fround;

/** `RPE_TWEEN_MAP` (index = RPE easingType 0..29). Also used for note controls and extra.json. */
export const RPE_TWEEN_MAP: readonly TweenId[] = [
  2, 2, 4, 3, 7, 6, 5, 8, 10, 9, 13, 12, 11, 14, 16, 15, 19, 18, 22, 21, 25, 24, 23, 26, 28, 27, 31, 30, 32, 29,
];

/** Highest RPE easingType with its own curve. */
export const MAX_RPE_EASING = 29;

/** Phira's slice tolerance `1e-5` as an f32 literal. */
export const SLICE_EPS = fr(1e-5);

/** All-zero bezier points (the RPE default `bezierPoints`). */
export const ZERO_BEZIER: BezierPoints = [0, 0, 0, 0];

/**
 * `RPE_TWEEN_MAP[max(easingType, 1)]`, or linear when the index is >= 30. easingType is an i32 in Phira;
 * non-integers are truncated and non-finite values count as out of range (linear).
 */
export function rpeToTweenId(easingType: number): TweenId {
  if (!Number.isFinite(easingType)) return LINEAR_TWEEN;
  const index = Math.max(Math.trunc(easingType), 1);
  return RPE_TWEEN_MAP[index] ?? LINEAR_TWEEN;
}

/** Inverse of RPE_TWEEN_MAP for ids 1..29 (null for plain ids and the two curves RPE has no number for). */
export function tweenIdToRpe(id: TweenId): number | null {
  if (id === LINEAR_TWEEN) return 1;
  for (let easingType = 2; easingType <= MAX_RPE_EASING; easingType++) {
    if (RPE_TWEEN_MAP[easingType] === id) return easingType;
  }
  return null;
}

function clamp01(v: number): number {
  // Rust f32::clamp: NaN stays NaN.
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** True when Phira uses the plain (static) tween for these resolved values (the slice is ignored). */
export function sliceIgnored(id: TweenId, left: number, right: number): boolean {
  return id <= LINEAR_TWEEN || (Math.abs(left) < SLICE_EPS && Math.abs(fr(right - 1)) < SLICE_EPS) || left >= right;
}

/** Phira's `RPEEvent::tween()` resolution (F§6.4). Bezier points are returned unchanged. */
export function resolveRpe(
  easingType: number,
  easingLeft: number,
  easingRight: number,
  bezier: number,
  bezierPoints: BezierPoints,
): PhiraTweenDesc {
  if (bezier !== 0) return { kind: 'bezier', p: bezierPoints };
  const tween = rpeToTweenId(easingType);
  const l = clamp01(fr(easingLeft));
  const r = clamp01(fr(easingRight));
  if (sliceIgnored(tween, l, r)) return { kind: 'static', tween };
  return { kind: 'clamped', tween, l, r };
}

/**
 * extra.json keyframe resolution (P§1.5, P§5.1): no clamping of the slice, no linear shortcut, no `l < r` check.
 * A slice is applied whenever it is not the default (0, 1) within 1e-5.
 */
export function resolveExtra(easingType: number, easingLeft = 0, easingRight = 1): PhiraTweenDesc {
  const tween = rpeToTweenId(easingType);
  const l = fr(easingLeft);
  const r = fr(easingRight);
  if (Math.abs(l) >= SLICE_EPS || Math.abs(fr(r - 1)) >= SLICE_EPS) return { kind: 'clamped', tween, l, r };
  return { kind: 'static', tween };
}
